import { BRAND_FAN_URL } from "@/lib/brandConfig";
import { AR_EXCLUSIVE_INVITE_PATH } from "@/lib/funnelPaths";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";

/**
 * IMPORTANTE — nomenclatura legacy:
 * - `birthday` (DB / Worker flag `showBirthday`) hoy se usa como **CTA "Comunidad"**
 *   apuntando a `/bestfriend-instagram`. Mantenemos la key del Worker para no requerir
 *   cambios en el Cloudflare Worker ni migración de DB.
 * - `community` (DB / Worker flag `showCommunity`, admin /admin/wishlist-config)
 *   = **CTA "Exclusivo"** → `/invitacion-exclusive` (InvitacionExclusivaMux + Stripe VIP).
 *   Ampliar cobertura: province_ar + city_ar en wishlist-config (city gana en el Worker).
 */
export type WorkerDrivenCtaVariant = "community" | "birthday" | "wishlist";

export type WorkerDrivenCtaResolution =
  | { mode: "hidden" }
  | { mode: "worker"; variant: WorkerDrivenCtaVariant; url: string }
  | { mode: "default" };

/**
 * URL del CTA "Comunidad" (variant `birthday`). Default: `/bestfriend-instagram` (página interna).
 * Override por brand vía env var: `VITE_BESTFRIEND_INSTAGRAM_CTA_URL` (puede ser absoluta o ruta).
 */
const COMUNIDAD_CTA_URL =
  (import.meta.env.VITE_BESTFRIEND_INSTAGRAM_CTA_URL as string | undefined)?.trim() ||
  "/bestfriend-instagram";

/**
 * Misma prioridad que el CTA del hero en BaseTemplate(Black):
 * hideCta → sin CTA; si no, showCommunity → /invitacion-exclusive ("Exclusivo");
 * showBirthday → /bestfriend-instagram ("Comunidad"); showWishlist → fan/wishlist;
 * si no hay flags, default (checkout / VIP lo define el caller).
 */
export function resolveWorkerDrivenCta(): WorkerDrivenCtaResolution {
  const cfg = getServerTemplateConfig();
  if (!cfg) return { mode: "default" };
  if (cfg.hideCta) return { mode: "hidden" };
  if (cfg.showCommunity) {
    return {
      mode: "worker",
      variant: "community",
      url: AR_EXCLUSIVE_INVITE_PATH,
    };
  }
  if (cfg.showBirthday) {
    return {
      mode: "worker",
      variant: "birthday",
      url: COMUNIDAD_CTA_URL,
    };
  }
  if (cfg.showWishlist) {
    return {
      mode: "worker",
      variant: "wishlist",
      url: `${BRAND_FAN_URL}/wishlist`,
    };
  }
  return { mode: "default" };
}

type Lang = "en" | "es" | "de" | "fr" | "it" | "pt" | "nl";

const WORKER_CTA_LABELS: Record<
  WorkerDrivenCtaVariant,
  Record<Lang, string>
> = {
  // `community` slot → "Exclusivo" (apunta a /invitacion-exclusive).
  community: {
    es: "Exclusivo",
    en: "Exclusive",
    pt: "Exclusivo",
    fr: "Exclusif",
    de: "Exklusiv",
    it: "Esclusivo",
    nl: "Exclusief",
  },
  // `birthday` slot → ahora etiquetado como "Comunidad" (apunta a /bestfriend-instagram).
  birthday: {
    es: "Comunidad",
    en: "Community",
    pt: "Comunidade",
    fr: "Communauté",
    de: "Community",
    it: "Community",
    nl: "Community",
  },
  wishlist: {
    es: "Ingresa a mi wishlist de cumpleaños",
    en: "Open my birthday wishlist",
    pt: "Ver minha lista de presentes",
    fr: "Voir ma wishlist d'anniversaire",
    de: "Zur Geburtstags-Wishlist",
    it: "Apri la mia wishlist di compleanno",
    nl: "Bekijk mijn verlanglijst",
  },
};

export function getWorkerCtaDisplayLabel(
  language: string | undefined,
  variant: WorkerDrivenCtaVariant,
): string {
  const key = (language?.split("-")[0]?.toLowerCase() as Lang) || "en";
  const row = WORKER_CTA_LABELS[variant];
  return row[key] ?? row.en;
}
